import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { AppBar } from './app-bar';
import { AddToHomeBar } from './add-to-home-bar';
import { SiteFooter } from './site-footer';
import { SignedInAccountProvider } from './signed-in-account';
import { TabBar, tabBarVisible } from './tab-bar';
import { ToastProvider } from './toast';
import { useLanguage } from './language-context';
import './shell.css';

// The two Listings pages (DR-81) draw the design's own header and footer inside the
// screen, so the shell steps back on them: no app bar, site footer or tab bar.
const LISTINGS_ROUTE = /^\/(?:search|business|b|l)(?:\/|$)/;
export function listingsRoute(pathname: string): boolean {
  return LISTINGS_ROUTE.test(pathname) && !/^\/(?:business|b)\/[^/]+\/book/.test(pathname);
}

// The persistent shell every screen renders into (CB-D2). App bar on top, the
// routed screen in the middle, the site footer below (desktop only, per design
// `showFooter = !isMobile`), and on mobile the tab bar, its sheets and — on home —
// the add-to-home-screen bar. `data-tabbar` lets CSS reserve room for the tab bar and
// lift the toast above it (design `toastLift`) only on routes that show it.
export function AppLayout() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const lr = listingsRoute(pathname);
  return (
    <SignedInAccountProvider>
      <ToastProvider>
        <div className="app-shell" data-tabbar={tabBarVisible(pathname) || undefined}>
          {/* First focusable element on the page, so a keyboard user can skip the header
              instead of tabbing through it on every navigation (§12). */}
          <a className="skip-link" href="#main">
            {t('nav.skip_to_content')}
          </a>
          {!lr && <AppBar />}
          {/* The Listings pages draw their own <main> (the design's landmark), so the
              shell's outlet is a plain container there — one main per page. */}
          {lr ? (
            <div className="app-main app-main--lr" id="main" tabIndex={-1}>
              <Outlet />
            </div>
          ) : (
            <main className="app-main" id="main" tabIndex={-1}>
              <Outlet />
            </main>
          )}
          {!lr && <SiteFooter />}
          {pathname === '/' && <AddToHomeBar />}
          {!lr && <TabBar />}
          <ScrollRestoration />
        </div>
      </ToastProvider>
    </SignedInAccountProvider>
  );
}
